import { useState } from "react";
import styles from "./ProjectTree.module.css";
import { downloadProjFile } from "@/service/file/FileService";
import { TexFileModel } from "@/model/file/TexFileModel";
import { AppState } from "@/redux/types/AppState";
import { useSelector } from "react-redux";
import React from "react";
import * as bootstrap from "bootstrap";
import TreeUpload from "../upload/TreeUpload";
import TreeFileMove from "../move/TreeFileMove";
import { SrcPosition } from "@/model/proj/pdf/SrcPosition";
import ProjFileSearch from "../search/ProjFileSearch";
import TeXSymbol from "../symbol/TeXSymbol";
import TreeFileRename from "../rename/TreeFileRename";
import TreeFileDel from "../del/TreeFileDel";
import TreeFileInfo from "../info/TreeFileInfo";
import { TreeProps } from "@/model/props/TreeProps";
import { ProjectTreeFolder } from "./ProjectTreeFolder";
import TreeFolderCreate from "../create/TreeFolderCreate";
import ProjectOutlinePanel from "../outline/ProjectOutlinePanel";
import Split from "@uiw/react-split";
import {
  handleExpandFolderEvent,
  handleFileAdd,
  handleFileCreateConfirm,
  handleFileSelected,
  handleFileTreeUpdate,
  handleProjSearch,
  handleProjSymbol,
  handleSrcFocusEditorLocate,
} from "./ProjectTreeHandler";
import { TeXFileType } from "@/model/enum/TeXFileType";
import { DownloadFileReq } from "@/model/request/file/query/DownloadFileReq";
import { useTranslation } from "react-i18next";
import { BaseMethods } from "rdjs-wheel";
import { QueryProjInfo } from "@/model/request/proj/query/QueryProjInfo.js";
import {
  getProjectInfo,
  projHistoryPage,
  setHistoryVersionFile,
} from "@/service/project/ProjectService.js";
import { QueryHistory } from "@/model/request/proj/query/QueryHistory.js";
import { defaultHistoryPageSize } from "@/config/app/global-conf.js";
import { Offcanvas } from "bootstrap";

const ProjectTree: React.FC<TreeProps> = (props: TreeProps) => {
  const divRef = props.treeDivRef;
  const [curTabName, setCurTabName] = useState("tree");
  const [createFileName, setCreateFileName] = useState("");
  const [texFileTree, setTexFileTree] = useState<TexFileModel[]>([]);
  const { fileTree, treeSelectItem, curFileTree, addFileResp } = useSelector(
    (state: AppState) => state.file
  );
  const { projInfo, srcFocus } = useSelector((state: AppState) => state.proj);
  const { curRootYDoc } = useSelector(
    (state: AppState) => state.projEditor
  );
  const pid = props.projectId;
  const selected = localStorage.getItem("proj-select-file:" + pid);
  const [selectedFile, setSelectedFile] = useState<TexFileModel>(
    selected ? JSON.parse(selected) : null
  );
  const [operFile, setOperFile] = useState<TexFileModel>();
  const [moveFile, setMoveFile] = useState<TexFileModel>();
  const [draggedNode, setDraggedNode] = useState<TexFileModel | null>(null);
  const [draggedOverNode, setDraggedOverNode] = useState<TexFileModel | null>(
    null
  );
  const { t } = useTranslation();

  React.useEffect(() => {
    if (treeSelectItem) {
      localStorage.setItem(
        "proj-select-file:" + props.projectId,
        JSON.stringify(treeSelectItem)
      );
      setSelectedFile(treeSelectItem);
    }
  }, [treeSelectItem, props.projectId]);

  React.useEffect(() => {
    if (projInfo && Object.keys(projInfo).length > 0) {
      handleFileTreeUpdate(projInfo.tree, props.projectId, setTexFileTree);
    }
  }, [projInfo]);

  React.useEffect(() => {
    if (!BaseMethods.isNull(addFileResp)) {
      // refresh the dependencies info by listen the add file action
      // when the access token invalid will refresh the access token and redispath this action
      // this will let the app refresh token silence
      let req: QueryProjInfo = {
        project_id: pid?.toString(),
      };
      getProjectInfo(req);
    }
  }, [addFileResp]);

  React.useEffect(() => {
    if (curFileTree && Object.keys(curFileTree).length > 0) {
      handleFileTreeUpdate(curFileTree, props.projectId, setTexFileTree);
    }
  }, [curFileTree]);

  React.useEffect(() => {
    if (fileTree && fileTree.length > 0) {
      handleFileTreeUpdate(fileTree, props.projectId, setTexFileTree);
    }
  }, [fileTree]);

  const handleHeaderAction = (id: string) => {
    let modal = document.getElementById(id);
    if (modal) {
      var myModal = new bootstrap.Modal(modal);
      myModal.show();
    }
  };

  const handleDownloadFile = async (file: TexFileModel) => {
    try {
      let req: DownloadFileReq = {
        file_id: file.file_id,
      };
      downloadProjFile(req).then((resp) => {
        const blob = new Blob([resp], { type: "application/oct-stream" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = file.name;
        link.click();
        window.URL.revokeObjectURL(url);
      });
    } catch (error) {
      console.error("Error downloading PDF:", error);
    }
  };

  const handleModal = (
    e: React.MouseEvent<HTMLDivElement>,
    show: boolean,
    modalId: string,
    file: TexFileModel
  ) => {
    e.preventDefault();
    e.stopPropagation();
    let modal = document.getElementById(modalId);
    if (modal) {
      setMoveFile(file);
      var myModal = new bootstrap.Modal(modal);
      show ? myModal.show() : myModal.hide();
    }
  };

  const showHistory = (file: TexFileModel) => {
    setHistoryVersionFile(file);
    const histQueryParam: QueryHistory = {
      project_id: file.project_id,
      page_size: defaultHistoryPageSize,
      file_int_id: file.id,
      flush: true,
    };
    projHistoryPage(histQueryParam);
    const offcanvasEl = document.getElementById("projHistory");
    if (offcanvasEl) {
      const bsOffcanvas = Offcanvas.getOrCreateInstance(offcanvasEl);
      bsOffcanvas.show();
    }
  };

  const renderIcon = (item: TexFileModel) => {
    if (item.file_type === TeXFileType.TEX) {
      return <i className="fa-regular fa-file"></i>;
    }
    if (item.file_type === TeXFileType.FOLDER) {
      return (
        <div className={styles.menuIcons}>
          <i
            className={
              item.expand
                ? `fa-solid fa-chevron-down ${styles.expandFlag}`
                : `fa-solid fa-chevron-right ${styles.expandFlag}`
            }
            onClick={(e: React.MouseEvent<HTMLElement>) => {
              handleExpandFolderEvent(e, item, texFileTree, setTexFileTree);
            }}
          ></i>
          <i className="fa-regular fa-folder"></i>
        </div>
      );
    }
  };

  const handleDragStart = (
    e: React.DragEvent<HTMLDivElement>,
    node: TexFileModel
  ) => {
    e.dataTransfer?.setData("text/plain", node.id.toString());
    setDraggedNode(node);
  };

  const handleDragOver = (
    e: React.DragEvent<HTMLDivElement>,
    node: TexFileModel
  ) => {
    e.preventDefault();
    e.stopPropagation();
    setDraggedOverNode(node);
  };

  const handleDrop = (
    e: React.DragEvent<HTMLDivElement>,
    targetNode: TexFileModel
  ) => {
    e.preventDefault();
    e.stopPropagation();
    if (draggedNode) {
      console.log(`Move node ${draggedNode.id} to ${targetNode.id}`);
    }
    setDraggedNode(null);
    setDraggedOverNode(null);
  };

  const handleDragLeave = (
    e: React.DragEvent<HTMLDivElement>,
    targetNode: TexFileModel
  ) => {
    if (draggedOverNode && targetNode.file_id === draggedOverNode?.file_id) {
      setDraggedOverNode(null);
    }
  };

  const handleSearchComplete = (paths: string[]) => {
    ProjectTreeFolder.handleExpandFolder(
      paths,
      props.projectId,
      selectedFile,
      curRootYDoc!
    );
  };

  const renderBody = () => {
    if (curTabName === "tree") {
      return (
        <div className={styles.treeBodySplit}>
          <Split
            mode="vertical"
            visible={[2, 3]}
            style={{ height: "100%", width: "100%" }}
          >
            <div className={styles.fileTreePane}>
              {renderDirectoryTree(texFileTree, 0)}
            </div>
            <div className={styles.outlinePane}>
              <ProjectOutlinePanel />
            </div>
          </Split>
        </div>
      );
    } else if (curTabName === "search") {
      return (
        <ProjFileSearch
          closeSearch={() => {
            setCurTabName("tree");
          }}
          searchComplete={handleSearchComplete}
          projectId={props.projectId}
        ></ProjFileSearch>
      );
    } else if (curTabName === "symbol") {
      return <TeXSymbol></TeXSymbol>;
    } else {
      return <div>not support</div>;
    }
  };

  const renderDirectoryTree = (fileTree: TexFileModel[], level: number) => {
    if (!fileTree) {
      return <div></div>;
    }
    const tagList: React.JSX.Element[] = [];
    const sortedData = fileTree.sort((a, b) => {
      if (a.file_type === 0 && b.file_type !== 0) {
        return -1;
      }
      if (a.file_type !== 0 && b.file_type === 0) {
        return 1;
      }
      return 0;
    });
    sortedData.forEach((item: TexFileModel) => {
      let expandStatus: boolean = ProjectTreeFolder.getExpandStatus(item);
      tagList.push(
        <div
          id={item.file_id}
          key={item.file_id}
          draggable={true}
          onDragStart={(e) => handleDragStart(e, item)}
          onDragOver={(e) => handleDragOver(e, item)}
          onDrop={(e) => handleDrop(e, item)}
          onDragLeave={(e) => handleDragLeave(e, item)}
        >
          <div
            key={item.file_id}
            onClick={(e: React.MouseEvent<HTMLDivElement>) =>
              handleTreeItemClick(e, item)
            }
            className={
              (selectedFile && item.file_id === selectedFile.file_id) ||
              (draggedOverNode && draggedOverNode.file_id === item.file_id)
                ? styles.fileItemSelected
                : styles.fileItem
            }
            style={{ paddingLeft: `${level === 0 ? 10 : level * 20 + 10}px` }}
          >
            {renderIcon(item)}
            <div className={styles.fileName}>{item.name}</div>
            <div className={styles.actions}>
              <div className="dropdown">
                <button
                  className="btn text-white"
                  type="button"
                  id={"dropdownMenuButton1" + item.id}
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                    handleDropdownClick(e, item);
                  }}
                >
                  <i className="fa-solid fa-ellipsis-vertical"></i>
                </button>
                <ul
                  className="dropdown-menu"
                  aria-labelledby={"dropdownMenuButton1" + item.id}
                >
                  <li>
                    <div
                      className="dropdown-item"
                      onClick={(e) => {
                        handleModal(e, true, "renameFileModal", item);
                      }}
                    >
                      {t("btn_rename")}
                    </div>
                  </li>
                  <li>
                    <div
                      className="dropdown-item"
                      onClick={(e) => {
                        handleDownloadFile(item);
                      }}
                    >
                      {t("btn_download")}
                    </div>
                  </li>
                  <li>
                    <div
                      className="dropdown-item"
                      onClick={(e) => {
                        handleModal(e, true, "moveFileModal", item);
                      }}
                    >
                      {t("btn_move_to_folder")}
                    </div>
                  </li>
                  <li>
                    <div
                      className="dropdown-item"
                      onClick={(e) => {
                        showHistory(item);
                      }}
                    >
                      {t("btn_show_history")}
                    </div>
                  </li>
                  <li>
                    <div
                      className="dropdown-item"
                      onClick={(e) => {
                        handleModal(e, true, "fileInfoModal", item);
                      }}
                    >
                      {t("btn_show_info")}
                    </div>
                  </li>
                  <li>
                    <div
                      className="dropdown-item"
                      onClick={(e) => {
                        handleModal(e, true, "deleteFileModal", item);
                      }}
                    >
                      {t("btn_del")}
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          {item.children && item.children.length > 0 && expandStatus ? (
            renderDirectoryTree(item.children, level + 1)
          ) : (
            <div></div>
          )}
        </div>
      );
    });
    return tagList;
  };

  const handleDropdownClick = (
    e: React.MouseEvent<HTMLButtonElement>,
    file: TexFileModel
  ) => {
    e.preventDefault();
    e.stopPropagation();
    setOperFile(file);
  };

  const handleTreeItemClick = (
    e: React.MouseEvent<HTMLDivElement>,
    fileItem: TexFileModel
  ) => {
    e.preventDefault();
    e.stopPropagation();
    handleFileSelected(fileItem, selectedFile, curRootYDoc);
  };

  const handleFileInputChange = (event: any) => {
    setCreateFileName(event.target.value);
  };

  React.useEffect(() => {
    // navigate from pdf to source
    if (srcFocus && srcFocus.length > 0) {
      let pos: SrcPosition = srcFocus[0];
      let name_paths = pos.file.split("/");
      ProjectTreeFolder.handleExpandFolder(
        name_paths,
        props.projectId,
        selectedFile,
        curRootYDoc!
      );
      handleSrcFocusEditorLocate(pos);
    }
  }, [srcFocus, props.projectId]);

  return (
    <div id="projTree" ref={divRef} className={styles.projTree}>
      <div className={styles.treeMenus}>
        <div>
          <button
            className={styles.menuButton}
            title={t("title_new_file")}
            onClick={() => {
              handleFileAdd();
            }}
          >
            <i className="fa-solid fa-file-circle-plus"></i>
          </button>
          <button
            className={styles.menuButton}
            title={t("title_create_folder")}
            onClick={() => {
              handleHeaderAction("createFolderModal");
            }}
          >
            <i className="fa-solid fa-folder-plus"></i>
          </button>
          <button
            className={styles.menuButton}
            title={t("title_upload_file")}
            onClick={() => {
              handleHeaderAction("uploadFileModal");
            }}
          >
            <i className="fa-solid fa-upload"></i>
          </button>
        </div>
        <div>
          <button
            className={styles.menuButton}
            title={t("title_collapse")}
            onClick={() => {
              let newTree = ProjectTreeFolder.handleCollapseAll(
                props.projectId
              );
              setTexFileTree(newTree);
            }}
          >
            <i className="fa-solid fa-minus"></i>
          </button>
          <button
            className={styles.menuButton}
            title={t("title_common_symbols")}
            onClick={() => {
              handleProjSymbol(curTabName, setCurTabName);
            }}
          >
            <i className="fa-solid fa-infinity"></i>
          </button>
          <button
            className={styles.menuButton}
            title={t("btn_search")}
            onClick={() => {
              handleProjSearch(curTabName, setCurTabName);
            }}
          >
            <i className="fa-solid fa-magnifying-glass"></i>
          </button>
        </div>
      </div>
      <div className={styles.treeBody}>{renderBody()}</div>
      <div
        className="modal fade"
        id="createFileModal"
        aria-labelledby="createModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="createModalLabel">
                {t("title_create_file")}
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <div className="input-group flex-nowrap">
                <input
                  id="folderName"
                  type="text"
                  onChange={handleFileInputChange}
                  className="form-control"
                  placeholder={t("tips_new_name")}
                  aria-label="Username"
                  aria-describedby="addon-wrapping"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                data-bs-dismiss="modal"
              >
                {t("btn_cancel")}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                data-bs-dismiss="modal"
                onClick={() => {
                  handleFileCreateConfirm(selectedFile, createFileName, pid);
                }}
              >
                {t("btn_confirm")}
              </button>
            </div>
          </div>
        </div>
      </div>
      <TreeFolderCreate
        projectId={pid}
        selectedFile={selectedFile}
      ></TreeFolderCreate>
      <TreeUpload projectId={pid}></TreeUpload>
      <TreeFileMove projectId={pid} texFile={moveFile!}></TreeFileMove>
      <TreeFileRename projectId={pid} operFile={operFile!}></TreeFileRename>
      <TreeFileDel projectId={pid} operFile={operFile!}></TreeFileDel>
      <TreeFileInfo operFile={operFile!}></TreeFileInfo>
    </div>
  );
};

export default ProjectTree;
